import type { Bounds, Mm } from '@bw/core';
import { EPSILON, MAX_SNAP_TOLERANCE_MM, SNAP_TOLERANCE_PX } from '@bw/core';

/**
 * A length in CSS pixels.
 *
 * This is where the brand from design document B2 earns its place: the
 * viewport is the only code that holds millimetres and pixels at once, and
 * mixing them is the classic defect in a zoomable drawing tool.
 */
export type Px = number & { readonly __px: unique symbol };
export const px = (value: number): Px => value as Px;

/** SPECIFICATION.md clause 11.2 — zoom limits. */
export const MIN_SCALE = 0.0005;
export const MAX_SCALE = 3.5;
/** The scale the zoom readout calls 100%. */
export const DEFAULT_SCALE = 0.75;
export const WHEEL_STEP = 1.12;
export const BUTTON_STEP = 1.2;
/** Clause 11.6 — cull to the viewport plus this much overscan. */
export const VIEWPORT_OVERSCAN_PX = 40;
/** Clause 11.3 — Fit adds this world margin on all sides. */
export const FIT_MARGIN_MM = 60;

export interface ViewportState {
  readonly scale: number;
  readonly originX: number;
  readonly originY: number;
}

/**
 * The world-to-screen transform (clause 11.1).
 *
 *   screenX = originX + worldX * scale
 *   screenY = originY - worldY * scale     <- Y is flipped
 */
export class Viewport {
  #scale = DEFAULT_SCALE;
  #originX = 78;
  #originY = 600;
  #width = 1;
  #height = 1;

  get scale(): number {
    return this.#scale;
  }

  get originX(): number {
    return this.#originX;
  }

  get originY(): number {
    return this.#originY;
  }

  get width(): Px {
    return px(this.#width);
  }

  get height(): Px {
    return px(this.#height);
  }

  get state(): ViewportState {
    return { scale: this.#scale, originX: this.#originX, originY: this.#originY };
  }

  set state(value: ViewportState) {
    this.#scale = clampScale(value.scale);
    this.#originX = value.originX;
    this.#originY = value.originY;
  }

  resize(width: number, height: number): boolean {
    const w = Math.max(1, Math.round(width));
    const h = Math.max(1, Math.round(height));
    if (w === this.#width && h === this.#height) return false;
    const first = this.#width === 1 && this.#height === 1;
    this.#width = w;
    this.#height = h;
    // Establish the initial view once the canvas has a real height (clause 15.1).
    if (first && h >= 200) {
      this.#originX = 78;
      this.#originY = h - 78;
    }
    return true;
  }

  toScreenX(x: Mm): Px {
    return px(this.#originX + x * this.#scale);
  }

  toScreenY(y: Mm): Px {
    return px(this.#originY - y * this.#scale);
  }

  toWorldX(x: Px | number): Mm {
    return (x - this.#originX) / this.#scale;
  }

  toWorldY(y: Px | number): Mm {
    return (this.#originY - y) / this.#scale;
  }

  /** Millimetres per pixel — the snap tolerance conversion (clause 6.1). */
  get snapToleranceMm(): Mm {
    return Math.min(MAX_SNAP_TOLERANCE_MM, SNAP_TOLERANCE_PX / this.#scale);
  }

  /** World rectangle currently visible, optionally grown by an overscan. */
  visibleBounds(overscanPx = 0): Bounds {
    return {
      minX: this.toWorldX(-overscanPx),
      minY: this.toWorldY(this.#height + overscanPx),
      maxX: this.toWorldX(this.#width + overscanPx),
      maxY: this.toWorldY(-overscanPx),
    };
  }

  /** Zoom about a screen anchor, keeping the world point under it fixed. */
  zoomAt(factor: number, anchorX: number, anchorY: number): boolean {
    const worldX = this.toWorldX(anchorX);
    const worldY = this.toWorldY(anchorY);
    const next = clampScale(this.#scale * factor);
    if (Math.abs(next - this.#scale) < EPSILON) return false;
    this.#scale = next;
    this.#originX = anchorX - worldX * next;
    this.#originY = anchorY + worldY * next;
    return true;
  }

  zoomBy(factor: number): boolean {
    return this.zoomAt(factor, this.#width / 2, this.#height / 2);
  }

  panBy(dx: number, dy: number): void {
    this.#originX += dx;
    this.#originY += dy;
  }

  /** Clause 11.3 — fit the wall, or reset when there is nothing to fit. */
  fit(bounds: Bounds | null): void {
    const width = Math.max(100, this.#width);
    const height = Math.max(100, this.#height);
    if (bounds === null) {
      this.#scale = DEFAULT_SCALE;
      this.#originX = 78;
      this.#originY = height - 78;
      return;
    }
    const contentWidth = bounds.maxX - bounds.minX + FIT_MARGIN_MM * 2;
    const contentHeight = bounds.maxY - bounds.minY + FIT_MARGIN_MM * 2;
    this.#scale = clampScale(
      Math.min((width - 30) / contentWidth, (height - 30) / contentHeight),
    );
    const centreX = (bounds.minX + bounds.maxX) / 2;
    const centreY = (bounds.minY + bounds.maxY) / 2;
    this.#originX = width / 2 - centreX * this.#scale;
    this.#originY = height / 2 + centreY * this.#scale;
  }

  /** Clause 11.2 — the zoom readout, relative to the default scale. */
  get zoomPercentText(): string {
    const percent = (this.#scale / DEFAULT_SCALE) * 100;
    return percent >= 1 ? `${percent.toFixed(0)}%` : `${percent.toFixed(2)}%`;
  }
}

export function clampScale(scale: number): number {
  if (!Number.isFinite(scale)) return DEFAULT_SCALE;
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
}
